import { Expand, Layers } from "lucide-react"
import { Icon, Tooltip, TooltipContent, TooltipTrigger } from "@nexus/ui"
import { useTranslation } from "react-i18next"
import type { CompetitorCreativeCardModel } from "../lib/drilldown-card.types"
import { formatYmdDotted, hasAdLibraryUrl } from "../lib/drilldown-row"
import { avatarColor } from "@/lib/avatar-color"
import { DrilldownCreativeCardShell } from "./DrilldownCreativeCardShell"
import { DrilldownCardBadge } from "./DrilldownCardBadge"
import { DrilldownCardStatBig, DrilldownCardStatCompact } from "./DrilldownCardStatRow"

const DAYS_PER_ASSET_DOT = "#6c4bff"
const ASSETS_DOT = "#464b67"

export interface DrilldownCompetitorCreativeCardProps {
  creative: CompetitorCreativeCardModel
  onOpenDrawer?: () => void
  /** Whole-card click; the preview moves to its own button. */
  onCardClick?: () => void
}

/**
 * Days and dates already show in the media badge and the "Active between" footer, so the
 * stats block carries the backend-computed Days/Assets ratio plus Assets. Assets is the only
 * place the asset count shows — grouped cards get no dup badge — so all cards in the grid
 * share one layout (AD-3166).
 */
export function DrilldownCompetitorCreativeCard({
  creative,
  onOpenDrawer,
  onCardClick,
}: DrilldownCompetitorCreativeCardProps) {
  const { t } = useTranslation()
  const mediaTypeLabel = creative.isCarousel
    ? t("common.mediaType.carousel", "Carousel")
    : creative.isVideo
      ? t("common.mediaType.video", "Video")
      : t("common.mediaType.image", "Image")

  return (
    <DrilldownCreativeCardShell
      thumbnailUrl={creative.thumbnailUrl}
      assetUrl={creative.assetUrl}
      isVideo={creative.isVideo}
      isCarousel={creative.isCarousel}
      carouselSlides={creative.carouselSlides}
      alt={creative.brandName}
      title={creative.brandName}
      logoSrc={creative.logoUrl ?? undefined}
      onStatsClick={onOpenDrawer}
      onCardClick={onCardClick}
      mediaOverlays={
        <>
          <div className="absolute left-1.5 top-1.5 flex items-center gap-1">
            <DrilldownCardBadge icon={creative.isVideo || creative.isCarousel ? "clapperboard" : "image"}>
              {mediaTypeLabel}
            </DrilldownCardBadge>
            <Tooltip>
              <TooltipTrigger asChild>
                {creative.logoUrl ? (
                  <img
                    src={creative.logoUrl}
                    alt={creative.brandName}
                    className="size-5 shrink-0 rounded-full object-cover shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_0_rgba(0,0,0,0.1)]"
                  />
                ) : (
                  <span
                    className="flex size-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold uppercase text-white shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_0_rgba(0,0,0,0.1)]"
                    style={{ backgroundColor: avatarColor(creative.competitor) }}
                  >
                    {creative.brandName.charAt(0)}
                  </span>
                )}
              </TooltipTrigger>
              <TooltipContent side="top">{creative.brandName}</TooltipContent>
            </Tooltip>
          </div>

          <div className="absolute right-1.5 top-1.5 flex items-center gap-1">
            {onOpenDrawer ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onOpenDrawer()
                }}
                aria-label={t("drilldown.drawer.openDetails", "Open creative details")}
                className="flex size-8 items-center justify-center rounded-lg border border-border/60 bg-white/90 text-gray-700 opacity-0 shadow-sm transition-opacity hover:text-foreground group-hover:opacity-100 dark:bg-black/60 dark:text-gray-200"
              >
                <Expand className="size-3.5" />
              </button>
            ) : null}
            {hasAdLibraryUrl(creative.adLibUrl) ? (
              <a
                href={creative.adLibUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                aria-label={t("common.openExternal")}
                className="flex size-6 items-center justify-center rounded-full bg-white/90 text-gray-700 transition-colors hover:text-foreground dark:bg-black/60 dark:text-gray-200"
              >
                <Icon name="external-link" size="xs" color="inherit" />
              </a>
            ) : null}
          </div>

          {creative.isCarousel ? (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <span className="flex size-8 items-center justify-center rounded-full bg-black/40 text-white ring-1 ring-white/25 backdrop-blur-[2px]">
                <Layers className="size-4" />
              </span>
            </div>
          ) : creative.isVideo ? (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-200 group-hover:opacity-0">
              <span className="flex size-8 items-center justify-center rounded-full bg-black/40 text-white ring-1 ring-white/25 backdrop-blur-[2px]">
                <svg viewBox="0 0 24 24" fill="currentColor" className="size-4" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </span>
            </div>
          ) : null}

          <div className="absolute bottom-1.5 left-1.5">
            <DrilldownCardBadge icon="calendar">
              {t("drilldown.card.daysActive", "{{count}} days active", { count: creative.days })}
            </DrilldownCardBadge>
          </div>
        </>
      }
      statsChildren={
        <div className="flex flex-col gap-0.5 py-1.5">
          <DrilldownCardStatBig
            dotColor={DAYS_PER_ASSET_DOT}
            label={t("drilldown.card.daysPerAsset", "Days/Assets")}
            value={creative.daysPerAsset.toFixed(1)}
          />
          <DrilldownCardStatCompact
            dotColor={ASSETS_DOT}
            label={t("drilldown.card.assets", "Assets")}
            value={String(creative.assets)}
          />
          <div className="flex items-center gap-1 px-2 pt-0.5">
            <Icon name="calendar" size="xs" color="inherit" className="text-muted-foreground" />
            <span className="text-[11px] text-muted-foreground">
              {t("drilldown.card.activeBetween", "Active between {{start}} – {{end}}", {
                start: formatYmdDotted(creative.minDate),
                end: formatYmdDotted(creative.maxDate),
              })}
            </span>
          </div>
        </div>
      }
    />
  )
}
